import { defineStore } from 'pinia'
import { SHA256 } from 'crypto-js'
import {
	resourceSchema,
	createResourceItem,
	readResourceItem,
	updateResourceItem,
	deleteResourceItem,
	RESOURCE_NAMES,
	convertToApiTimeDate,
	parseApiTimeDate,
} from '@/api/api.js'
import { USER_ROLES_SCOPE } from '@/app.conf.js'


//
const ACCOUNT_ITEM_KEY = "account"


export const useAccontStore = defineStore('accountStore', {
    state: () => ({
		account: {}
	}),

    getters: {
		schema() {
			return {
				login: 'login',
				password: 'password',
				name: 'name',
				phone: 'phone',
				position: 'position',
				organizationId: 'organization_id',
				organization: {
					self: 'organization',
					title: 'title',
					role: 'role',
					departmentTitle: 'department_title',
					sectorTitle: 'sector_title',
					departments: {
						self: 'departments',
						id: 'id',
						title: 'title',
						sectors: 'sectors',
					},
					pipelines: 'pipelines',
					map: 'map',
					relationGraph: 'relation_graph',
					mapApiKey: 'map_api_key',
					influxApiKey: 'influx_api_key',
					gotifyUser: 'gotify_user',
					gotifyPass: 'gotify_pass',
				},				
				departmentsScope: 'departments_scope',
				sectorsScope: 'sectors_scope',
				role: 'role', // 'allowed': ['admin', 'manager', 'dispatcher', 'visitor'],
				isEnabled: 'isEnabled',
				gotify: {
					self: 'gotify',
					userId: 'user_id',
					testToken: 'test_token',
					stationsToken: 'stations_token',
					postsToken: 'posts_token',
				},
				settings: {
					self: 'settings',
					stationsColumns: 'stations_columns',
					stationsSorting: 'stations_sorting',
					stationsView: 'stations_view',
					postColumns: 'post_columns',
					eventsAcknowledgeTime: 'events_acknowledge_time',
				},
			}
		},

		userId() { return this.account[resourceSchema.id.key] },

		userLogin() { return this.account.login },

		userPassword() { return this.account.password },
		
		userName() { return this.account.name ? this.account.name : '' },

		organizationRoles() {
			return Object.freeze({
				primary: 'primary',
				secondary: 'secondary',
			})
		},

        organization() {
			// Returns organization Object
            return this.account[this.schema.organization.self] ?
				this.account[this.schema.organization.self] :
				{}
        },

		departmentsScope() {
			return this.account[this.schema.departmentsScope] ?
			this.account[this.schema.departmentsScope] :
			[]
		},

		sectorsScope() {
			return this.account[this.schema.sectorsScope] ?
			this.account[this.schema.sectorsScope] :
			[]
		},

		isPrimary() {
			return this.organization[this.schema.organization.role] === this.organizationRoles.primary
		},

		permissions() {
			if (this.account.role) {
				return USER_ROLES_SCOPE[this.account.role]
			} else {
				return []
			}
		},

		// Return stations view type
		stationsView() {
			if (!this.account[this.schema.settings.self] ||
				!this.account[this.schema.settings.self][this.schema.settings.stationsView]) {
				this.account[this.schema.settings.self][this.schema.settings.stationsView] = 'table'
			}
			
			return this.account[this.schema.settings.self][this.schema.settings.stationsView]
		},

		stationViewTypes() {
			return Object.freeze({
				table: 'table',
				tiles: 'tiles',
			})
		},

		// Return stations table culumns 
		stationsColumns() {
			if (this.account[this.schema.settings.self] && 
				this.account[this.schema.settings.self][this.schema.settings.stationsColumns]) {
				return this.account[this.schema.settings.self][this.schema.settings.stationsColumns]
			} else {
				this.account[this.schema.settings.self][this.schema.settings.stationsColumns] = new Object()
				return this.account[this.schema.settings.self][this.schema.settings.stationsColumns]
			}
		},

		// Return stations sorting settings
		stationsSorting() {
			if (this.account[this.schema.settings.self] && 
				this.account[this.schema.settings.self][this.schema.settings.stationsSorting]) {
				return this.account[this.schema.settings.self][this.schema.settings.stationsSorting]
			} else {
				this.account[this.schema.settings.self][this.schema.settings.stationsSorting] = {
					sortBy: 'name',
					sortType: 'asc',
				}
				return this.account[this.schema.settings.self][this.schema.settings.stationsSorting]
			}
		},

		// Return posts table culumns 
		postsColumns() {
			if (this.account[this.schema.settings.self] && 
				this.account[this.schema.settings.self][this.schema.settings.postColumns]) {
				return this.account[this.schema.settings.self][this.schema.settings.postColumns]
			} else {
				this.account[this.schema.settings.self][this.schema.settings.postColumns] = new Object()
				return this.account[this.schema.settings.self][this.schema.settings.postColumns]
			}
		},

		eventsAcknowledgeTime() {
			return this.account[this.schema.settings.self][this.schema.settings.eventsAcknowledgeTime] ?? undefined
		},

		etag() { return this.account[resourceSchema.etag.key] },

		_isDefine() {
			return this.account ? true : false
		},
    },

    actions: {
        initAccount(account) {
            this.account = account
            this._saveToLocalstorage()
        },

		async createAccount(account) {
			account[this.schema.password] = 
				await SHA256(account[this.schema.password]).toString()
			// Request server to create account
			return await createResourceItem(
				RESOURCE_NAMES.accounts,
				this.userLogin,
				this.userPassword,
				account,
			)
		},

		async updateAccount(account) {
			const payload = structuredClone(account)
			const etag = payload[resourceSchema.etag.key]
			delete payload[resourceSchema.etag.key]

			if (payload[this.schema.password]) {
				payload[this.schema.password] = 
					await SHA256(payload[this.schema.password]).toString()
			}
			
			return await updateResourceItem(
				RESOURCE_NAMES.accounts,
				this.userLogin,
				this.userPassword,
				payload[resourceSchema.id.key],
				etag,
				payload,
			)
		},

		async deleteAccount(userId, etag) {
			return await deleteResourceItem(
				RESOURCE_NAMES.accounts,
				this.userLogin,
				this.userPassword,
				userId,
				etag,
			)
		},

		cleanAccount() {
			this.account = {}
			this._saveToLocalstorage()
		},

		switchStationsView(newView) {
			if (!Object.values(this.stationViewTypes).includes(newView)) {
				console.error(`AccountStore -> switchStationsView: incorrect view type "${newView}"`)
				return
			}

			this.account[this.schema.settings.self][this.schema.settings.stationsView] = newView
		},

		async updateStationColumns(columns) {
			this.account[this.schema.settings.self][this.schema.settings.stationsColumns] = null
			await this.save([this.schema.settings.self])

			this.account[this.schema.settings.self][this.schema.settings.stationsColumns] = Object.assign({}, columns)
			await this.save([this.schema.settings.self])
			
			// console.log("updateStationColumns to ", columns)
		},

		updatePostColumns(columns) {
			this.account[this.schema.settings.self][this.schema.settings.postColumns] = Object.assign({}, columns)
			//console.log("updatePostColumns to ", this.account.settings[this.schema.postColumns])
		},

		async acknowledgeEvents() {
			this.account[this.schema.settings.self][this.schema.settings.eventsAcknowledgeTime] = new Date()
			await this.save([this.schema.settings.self])
		},

		async load() {
			// Get account from Local Storage
			const lsAccount = JSON.parse(localStorage.getItem(ACCOUNT_ITEM_KEY))
			if (!lsAccount || !lsAccount.login || !lsAccount.password) {
				return false
			}
			// console.log("Account storage: load account from server")

			const result = await readResourceItem(
				RESOURCE_NAMES.accounts,
				lsAccount.login,
				lsAccount.login,
				lsAccount.password,
				[`embedded={"${this.schema.organizationId}":1}`,]
			).then(response => {
				// console.log("Account storage: load acc ount from server, response: ", response)
				if (response[this.schema.password] === lsAccount[this.schema.password]) {
					this.account = structuredClone(response)

					// Save organization object to predetermined field
					this.account[this.schema.organization.self] = { ... this.account[this.schema.organizationId] }
					this.account[this.schema.organizationId] = this.account[this.schema.organization.self][resourceSchema.id.key]

					// Convert datetime fields to Date
					if (this.account[this.schema.settings.self][this.schema.settings.eventsAcknowledgeTime]) {
						this.account[this.schema.settings.self][this.schema.settings.eventsAcknowledgeTime] = parseApiTimeDate(
							this.account[this.schema.settings.self][this.schema.settings.eventsAcknowledgeTime]
						)
					}
					
					return true
				} else {
					return false
				}
			}).catch(error => {
				console.error(`on load user account from server: "${error}".`)
				return false
			})

			return result
		},

		async save(fields=[]) {
			// console.log("Account storage: save account", fields)
			let result = true

			// Save to server
			if (this._isDefine && this.account[this.schema.login] &&
				this.account[this.schema.password]) {
				result = await this._saveToServer(fields)
			}

			// Save to Local Storage
			this._saveToLocalstorage()

			return result
		},

		async _saveToServer(fields) {
			let payload
			
			// Filter fields if need
			if (fields.length > 0) {
				payload = Object.keys(this.account)
				.filter(key => fields.includes(key))
				.reduce((obj, key) => {
					obj[key] = this.account[key];
					return obj;
				}, {});
			} else {
				// Unwrap and delete service values
				payload = this._unwrapObject(this.account)
			}

			// Add organization id
			delete payload[this.schema.organization.self]

			// Enumerate all schema. Translate all Date to API datetime
			for (const [key, value] of Object.entries(payload)) {
				if (value instanceof Date && this.account[key]) {
					payload[key] = convertToApiTimeDate(value)
				}
			}
			for (const [key, value] of Object.entries(payload[this.schema.settings.self])) {
				if (value instanceof Date && this.account[this.schema.settings.self][key]) {
					payload[this.schema.settings.self][key] = convertToApiTimeDate(value)
				}
			}
			// delete payload[this.schema.settings.self][this.schema.settings.eventsAcknowledgeTime]
			// console.log("Payload after copy = ", payload)

			return await updateResourceItem(
				RESOURCE_NAMES.accounts,
				this.userLogin,
				this.userPassword,
				this.account[resourceSchema.id.key],
				this.account[resourceSchema.etag.key],
				payload
			).then(result => {
				//console.log("update storage result = ", result)
				this.account[resourceSchema.etag.key] = result[resourceSchema.etag.key]
				this.account[resourceSchema.updateTime.key] = result[resourceSchema.updateTime.key]
				return true
			}).catch(err => {
				console.error(err)
				return false
			})
		},

		_saveToLocalstorage() {
			localStorage.setItem(
				ACCOUNT_ITEM_KEY,
				JSON.stringify(this.account)
			)
		},

		// Unwrap input object and nested objects.
		// Delete from payload all service values (key start from "_")
		// !!! Work normal only with Object (no Array, no Map) !!!
		_unwrapObject(obj) {	
			let unwrapped = new Object()
			
			Object.entries(obj).forEach(([key, value]) => {
				// If service property
				if (key[0] === '_') {
					return
				}

				// If Date
				if (value instanceof Date) {
					unwrapped[key] = new Date(value)
				// If nested object
				} else if (typeof value === 'object') {
					unwrapped[key] = this._unwrapObject(value)
				} else {
					unwrapped[key] = value
				}
			});

			return unwrapped
		},
    },
})
